import { useMemo } from "react";
import { useAgent } from "@/hooks/agent";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import type {
  KnowledgeSource,
  KnowledgeSourceKind,
  KnowledgeSourceStatus,
  KnowledgeViewResult,
} from "@/types/voice-vision";

/* ─────────────────── document -> KnowledgeSource mapping ─────────────────── */

/** Map a raw document source_type to the read-only view kind. */
function mapSourceType(sourceType?: string | null): KnowledgeSourceKind {
  switch (sourceType) {
    case "pdf_upload":
      return "pdf";
    case "document_upload":
      return "doc";
    case "website":
    case "website-scrape":
      return "website";
    case "notes":
      return "note";
    default:
      return "doc";
  }
}

/** Short, plain-language source label for the meta line. */
function kindLabel(kind: KnowledgeSourceKind): string {
  switch (kind) {
    case "pdf":
      return "PDF";
    case "website":
      return "Website";
    case "note":
      return "Note";
    case "faq":
      return "FAQ";
    case "doc":
    default:
      return "Doc";
  }
}

/** Human file size, e.g. "1.4 MB" / "320 KB". */
function humanSize(bytes?: number): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Plain-language "synced today / yesterday / N days ago" from an ISO time. */
function syncedPhrase(iso?: string | null): string {
  if (!iso) return "synced recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "synced recently";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "synced today";
  if (days === 1) return "synced yesterday";
  if (days < 7) return `synced ${days} days ago`;
  if (days < 14) return "synced last week";
  if (days < 31) return `synced ${Math.floor(days / 7)} weeks ago`;
  return "synced last month";
}

/**
 * Build a meta line from source_type + metadata, e.g.
 * "PDF · 12 pages · synced today" or "Doc · 1.4 MB · synced yesterday".
 */
function buildMetaLine(doc: DocumentWithMetadata): string {
  const kind = mapSourceType(doc.source_type);
  const parts: string[] = [kindLabel(kind)];

  const pages = doc.metadata?.total_pages;
  const size = humanSize(doc.metadata?.pdf_size);
  if (pages && pages > 0) {
    parts.push(`${pages} ${pages === 1 ? "page" : "pages"}`);
  } else if (size) {
    parts.push(size);
  }

  parts.push(syncedPhrase(doc.updated_at ?? doc.created_at));
  return parts.join(" · ");
}

/** Map one document into the read-only KnowledgeSource shape. */
function toKnowledgeSource(doc: DocumentWithMetadata, genieId: string): KnowledgeSource {
  return {
    id: doc.id,
    genieId,
    name: doc.name,
    kind: mapSourceType(doc.source_type),
    meta: buildMetaLine(doc),
    status: (doc.status as KnowledgeSourceStatus) ?? "synced",
    // No separate synced timestamp on the document — last update is the
    // last successful sync; fall back to creation time.
    syncedAt: doc.updated_at ?? doc.created_at ?? new Date().toISOString(),
  };
}

/* ─────────────────────────────── hook ──────────────────────────────────── */

/**
 * Read-only knowledge sources for one genie (owner Knowledge tab).
 *
 * Composes the real per-genie document store and the genie record, then maps
 * each document into the KnowledgeViewResult/KnowledgeSource shape the owner
 * Knowledge + Overview surfaces consume. Field names are preserved so the
 * Overview knowledge card keeps working unchanged.
 */
export const useGenieKnowledge = (id: string) => {
  const {
    data: documents = [],
    isLoading,
    isError,
  } = useDocuments("my-documents", { agentId: id }, undefined, !!id);

  const { data: agent } = useAgent(id);

  const data = useMemo<KnowledgeViewResult>(() => {
    const sources = documents.map((doc) => toKnowledgeSource(doc, id));

    const description = agent?.description?.trim();
    const coverageNote =
      description && description.length > 0
        ? description
        : "Drawn from the documents and notes you have given this genie.";

    return {
      sources,
      total: sources.length,
      coverageNote,
    };
  }, [documents, agent?.description, id]);

  return { data, isLoading, isError };
};
